import { auth } from '../firebase';

export class CloudinaryApiError extends Error {
  constructor(message, code = 'CLOUDINARY_FAILED', status = 0) {
    super(message);
    this.name = 'CloudinaryApiError';
    this.code = code;
    this.status = status;
  }
}

const safeJson = async (response) => {
  const text = await response.text();
  try { return text ? JSON.parse(text) : {}; } catch (_error) { return {}; }
};

const token = async () => {
  const value = await auth.currentUser?.getIdToken();
  if (!value) throw new CloudinaryApiError('Tu sesión no está disponible. Vuelve a iniciar sesión.', 'AUTH_REQUIRED', 401);
  return value;
};

export async function requestCloudinaryUploadSignature({ purpose, file }) {
  const authToken = await token();
  const response = await fetch('/api/cloudinary/sign-upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({
      purpose,
      fileName: file?.name || 'imagen',
      contentType: file?.type || 'image/webp',
      bytes: Number(file?.size || 0),
    }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw new CloudinaryApiError(data?.error || 'No se pudo autorizar la imagen.', data?.code, response.status);
  return data;
}

export function uploadSignedImage(file, signed, { onProgress } = {}) {
  return new Promise((resolve, reject) => {
    if (!signed?.cloudName || !signed?.signature || !signed?.publicId) {
      reject(new CloudinaryApiError('La autorización de la imagen no es válida.'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${encodeURIComponent(signed.cloudName)}/image/upload`);
    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onerror = () => reject(new CloudinaryApiError('No se pudo subir la imagen por un problema de red.', 'CLOUDINARY_NETWORK_ERROR'));
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText || '{}'); } catch (_error) {}
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new CloudinaryApiError('Cloudinary rechazó la imagen.', 'CLOUDINARY_UPLOAD_FAILED', xhr.status));
        return;
      }
      if (data.public_id !== signed.publicId || !data.secure_url) {
        reject(new CloudinaryApiError('La respuesta de la imagen no coincide con la autorización.', 'CLOUDINARY_UPLOAD_MISMATCH'));
        return;
      }
      resolve(data);
    };

    const form = new FormData();
    form.append('file', file, file?.name || 'imagen');
    form.append('api_key', signed.apiKey);
    form.append('timestamp', String(signed.timestamp));
    form.append('signature', signed.signature);
    const params = signed.uploadParams || {};
    Object.entries(params).forEach(([key, value]) => {
      if (key === 'timestamp' || value == null) return;
      form.append(key, typeof value === 'boolean' ? String(value) : String(value));
    });
    xhr.send(form);
  });
}

export async function uploadImage(file, purpose, options = {}) {
  const signed = await requestCloudinaryUploadSignature({ purpose, file });
  return uploadSignedImage(file, signed, options);
}

export async function getExpenseSupportUrl(expenseId) {
  const authToken = await token();
  const response = await fetch('/api/cloudinary/sign-view', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ expenseId }),
  });
  const data = await safeJson(response);
  if (!response.ok || !data?.signedUrl) throw new CloudinaryApiError(data?.error || 'No se pudo abrir el soporte.', data?.code, response.status);
  return data;
}

export async function deleteExpenseSupport(expenseId) {
  const authToken = await token();
  const response = await fetch('/api/cloudinary/delete', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ expenseId }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw new CloudinaryApiError(data?.error || 'No se pudo eliminar el soporte.', data?.code, response.status);
  return data;
}
